/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from "react";

import { TutorialInstructionSteps } from "@/templates/agile-hub/tutorial-types";

import {
    AgileTutorialBodyContent,
    UniformBodyContentProps
} from "./src/agile-tutorial-body-content";

export type { UniformBodyContentProps } from "./src/agile-tutorial-body-content";

export const format = (
    body: UniformBodyContentProps
): TutorialInstructionSteps => {
    if (!body)
        return { tutorialUseStepTitle: null, tutorialBodyTextContainer: null };

    const { tutorialInstructionSteps }: any = body;

    const formattedBody: TutorialInstructionSteps = {
        tutorialUseStepTitle:
            tutorialInstructionSteps?.tutorialUseStepTitleExternal ?? null,
        tutorialBodyTextContainer:
            formatContentComponent(
                tutorialInstructionSteps?.tutorialBodyTextContainer
            ) ?? null
    };

    return formattedBody;
};

const formatContentComponent = (contentComponents: any[]) => {
    if (!contentComponents) return [];

    const formattedContentComponents = contentComponents.map(
        // eslint-disable-next-line complexity
        (contentComponent) => {
            if (contentComponent.sys.contentType.sys.id === "textComponent") {
                return {
                    __typename: "TextComponent",
                    name: contentComponent.fields?.textComponentNameExternal,
                    text: contentComponent.fields?.text
                };
            } else if (
                contentComponent.sys.contentType.sys.id === "assetComponent"
            ) {
                return {
                    __typename: "AssetComponent",
                    asset: formatAsset(contentComponent.fields?.asset),
                    caption: contentComponent.fields?.assetCaption,
                    assetRelatedText: contentComponent.fields?.assetRelatedText
                };
            } else if (
                contentComponent.sys.contentType.sys.id === "proTipComponent"
            ) {
                let proTipText = "";
                if (contentComponent.fields?.proTipRichText) {
                    proTipText = contentComponent.fields?.proTipRichText;
                } else if (contentComponent.fields?.proTipText) {
                    proTipText = contentComponent.fields?.proTipText;
                } else if (contentComponent.fields?.proTipTextShort) {
                    proTipText = contentComponent.fields?.proTipTextShort;
                }
                return {
                    __typename: "ProTipComponent",
                    name: contentComponent.fields?.proTipNameExternal,
                    proTipText: proTipText
                };
            } else if (
                contentComponent.sys.contentType.sys.id === "embeddedVideoAsset"
            ) {
                return {
                    __typename: "EmbeddedVideoAsset",
                    embeddedVideoAssetName:
                        contentComponent.fields?.embeddedVideoAssetNameExternal,
                    embedded: contentComponent.fields?.embedded,
                    embedAssetOverlay: formatAsset(
                        contentComponent.fields?.embedAssetOverlay
                    )
                };
            } else if (
                contentComponent.sys.contentType.sys.id === "twitterComponent"
            ) {
                return {
                    __typename: "TwitterComponent",
                    twitterComponentName:
                        contentComponent.fields?.twitterComponentExternalName,
                    tweetText: contentComponent.fields?.tweetText,
                    twitterUrl: contentComponent.fields?.twitterUrl
                };
            } else if (
                contentComponent.sys.contentType.sys.id === "callToAction"
            ) {
                return {
                    __typename: "CallToAction",
                    title: contentComponent.fields?.ctaTitleExternal,
                    valueProposition: contentComponent.fields?.valueProposition,
                    ctaText: contentComponent.fields?.ctaText,
                    url: contentComponent.fields?.ctaUrl,
                    asset: formatAsset(contentComponent.fields?.ctaAsset),
                    product: contentComponent.fields?.product,
                    dataBlueprintModule:
                        contentComponent.fields?.dataBlueprintModule
                };
            } else if (
                contentComponent.sys.contentType.sys.id === "questionComponent"
            ) {
                return {
                    __typename: "QuestionComponent",
                    questionTitle:
                        contentComponent.fields?.questionTitleExternal,
                    answer: contentComponent.fields?.answer,
                    answerAsset: formatAsset(
                        contentComponent.fields?.answerAsset
                    )
                };
            } else if (
                contentComponent.sys.contentType.sys.id === "callOutComponent"
            ) {
                return {
                    __typename: "CallOutComponent",
                    title: contentComponent.fields
                        ?.callOutComponentTitleExternal,
                    callOutText: contentComponent.fields?.callOutText,
                    icon: formatAsset(
                        contentComponent.fields?.callOutComponentIcon
                    )
                };
            } else {
                // Default case for unknown component types
                return {
                    __typename: "UnknownComponent",
                    ...contentComponent
                };
            }
        }
    );

    // Filter out unknown component types
    return formattedContentComponents.filter(
        (component) => component.__typename !== "UnknownComponent"
    );
};

// eslint-disable-next-line complexity
const formatAsset = (asset: any) => {
    if (!asset?.fields) return undefined;

    const { fields } = asset;
    return {
        name: fields.title,
        size: fields.file?.details?.size,
        fileName: fields.file?.fileName ?? null,
        height: fields.file?.image?.height ?? null,
        width: fields.image?.width ?? null,
        url: fields.file?.url,
        description: fields.description ?? null,
        contentType: fields.file?.contentType ?? null
    };
};

const UniformAgileTutorialBodyContent = (props: UniformBodyContentProps) => {
    const tutorialInstructionSteps = useMemo(() => format(props), [props]);

    return (
        <AgileTutorialBodyContent
            {...props}
            tutorialInstructionSteps={tutorialInstructionSteps}
        />
    );
};

export default UniformAgileTutorialBodyContent;
